import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  BookOpen, Search, RefreshCw, Copy, Check, Filter, X, 
  ArrowUpRight, ArrowDownLeft, Sparkles, FileText, Gift, 
  CreditCard, RotateCcw, User, Loader2, Coins
} from 'lucide-react';
import { useAdminLedger } from '../../hooks/useQueries';
import { useDebounce } from '../../hooks/useDebounce';
import { ServerPagination } from '../ui/ServerPagination';
import { SystemLedgerTransaction } from '../../types';
import { useQueryClient } from '@tanstack/react-query';

export const LedgerTable: React.FC = () => {
  const queryClient = useQueryClient();

  // 1. Initial State from URL
  const getInitialParams = () => {
    if (typeof window === 'undefined') {
      return { page: 1, limit: 10, search: '', type: 'all' };
    }
    const searchParams = new URLSearchParams(window.location.search);
    return {
      page: parseInt(searchParams.get('l_page') || '1', 10) || 1,
      limit: parseInt(searchParams.get('l_limit') || '10', 10) || 10,
      search: searchParams.get('l_search') || '',
      type: searchParams.get('l_type') || 'all'
    };
  };

  const initialParams = useMemo(() => getInitialParams(), []);

  const [currentPage, setCurrentPage] = useState<number>(initialParams.page);
  const [pageSize, setPageSize] = useState<number>(initialParams.limit);
  const [searchQuery, setSearchQuery] = useState<string>(initialParams.search);
  const [typeFilter, setTypeFilter] = useState<string>(initialParams.type);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Debounce search input to avoid spamming requests
  const debouncedSearch = useDebounce(searchQuery, 400);
  const isMountedRef = useRef<boolean>(false);

  // 2. React Query Hook for Centralized Ledger
  const {
    data: ledgerQueryData,
    isLoading,
    isFetching,
    refetch
  } = useAdminLedger({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearch,
    type: typeFilter
  });

  const transactions: SystemLedgerTransaction[] = ledgerQueryData?.data || [];
  const totalRecords = ledgerQueryData?.pagination?.totalCount ?? 0;
  const totalPages = ledgerQueryData?.pagination?.totalPages ?? 1;

  // 3. Sync Filters with Browser URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (currentPage > 1) {
        url.searchParams.set('l_page', String(currentPage));
      } else {
        url.searchParams.delete('l_page');
      }

      if (pageSize !== 10) {
        url.searchParams.set('l_limit', String(pageSize));
      } else {
        url.searchParams.delete('l_limit');
      }

      if (debouncedSearch.trim()) {
        url.searchParams.set('l_search', debouncedSearch.trim());
      } else {
        url.searchParams.delete('l_search');
      }

      if (typeFilter !== 'all') {
        url.searchParams.set('l_type', typeFilter);
      } else {
        url.searchParams.delete('l_type');
      }

      window.history.replaceState(null, '', url.toString());
    }
  }, [currentPage, pageSize, debouncedSearch, typeFilter]);

  // Reset to page 1 on search or filter change
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearch, typeFilter]);

  // Copy Reference ID Helper
  const handleCopy = (text: string, id: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  // Helper for Type Badge Rendering
  const renderTypeBadge = (type: string) => {
    const t = (type || '').toUpperCase();
    
    if (t === 'BANK_DEPOSIT' || t === 'TOPUP') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CreditCard className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          <span>Nạp Ngân Hàng</span>
        </span>
      );
    }

    if (t === 'ADMIN_BONUS' || t === 'BONUS') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
          <Gift className="w-3.5 h-3.5 text-purple-600 shrink-0" />
          <span>Thưởng Admin</span>
        </span>
      );
    }

    if (t === 'DOWNLOAD_DOC' || t === 'DOCUMENT_DOWNLOAD') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
          <FileText className="w-3.5 h-3.5 text-blue-600 shrink-0" />
          <span>Tải Tài Liệu</span>
        </span>
      );
    }

    if (t === 'USAGE_AI' || t === 'AI_USAGE') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
          <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0" />
          <span>Sử Dụng AI</span>
        </span>
      );
    }

    if (t === 'REFUND') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
          <RotateCcw className="w-3.5 h-3.5 text-teal-600 shrink-0" />
          <span>Hoàn Xu</span>
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
        <Coins className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        <span>{type || 'Giao dịch'}</span>
      </span>
    );
  };

  const typeOptions = [
    { value: 'all', label: 'Tất cả loại giao dịch' },
    { value: 'BANK_DEPOSIT', label: 'Nạp Ngân Hàng (BANK_DEPOSIT / TOPUP)' },
    { value: 'ADMIN_BONUS', label: 'Thưởng Admin (ADMIN_BONUS / BONUS)' },
    { value: 'DOWNLOAD_DOC', label: 'Tải Tài Liệu (DOWNLOAD_DOC)' },
    { value: 'USAGE_AI', label: 'Sử Dụng AI (USAGE_AI)' },
    { value: 'REFUND', label: 'Hoàn Xu (REFUND)' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden space-y-0 animate-fade-in">
      {/* 1. Header Toolbar */}
      <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              <span>Sổ Cái Biến Động Toàn Hệ Sinh Thái (Centralized Ledger)</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Đối soát toàn bộ dòng tiền Xu, nạp rút, tiêu dùng và thưởng của mọi thành viên ({totalRecords} giao dịch)
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              refetch();
              queryClient.invalidateQueries({ queryKey: ['adminLedger'] });
            }}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors self-end sm:self-auto flex items-center space-x-1.5 text-xs font-bold cursor-pointer"
            title="Làm mới Sổ Cái"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-indigo-600' : ''}`} />
            <span className="hidden sm:inline">Làm mới</span>
          </button>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          {/* Search Box */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nhập ssoUid, Email, Tên user, mã Ref hoặc nội dung..."
              className="w-full pl-9 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded-full cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Type Filter Dropdown */}
          <div className="w-full sm:w-72">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all cursor-pointer"
            >
              {typeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 2. Table Data */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50/90 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              <th className="py-3.5 px-4 w-40">Thời gian</th>
              <th className="py-3.5 px-4 w-60">Người dùng</th>
              <th className="py-3.5 px-4 w-44">Loại Giao Dịch</th>
              <th className="py-3.5 px-4 w-40">Tham chiếu (Ref)</th>
              <th className="py-3.5 px-4 w-32 text-right">Biến động</th>
              <th className="py-3.5 px-4 w-32 text-right">Số dư cuối</th>
              <th className="py-3.5 px-4 min-w-[220px]">Chi tiết</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="py-14 text-center text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2.5 text-indigo-600" />
                  <span className="font-medium text-xs">Đang tải dữ liệu Sổ Cái từ hệ thống...</span>
                </td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-14 text-center text-slate-400">
                  <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-2">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <div className="font-bold text-slate-700 text-xs">Không tìm thấy giao dịch nào</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {searchQuery || typeFilter !== 'all'
                      ? 'Hãy thử thay đổi từ khóa tìm kiếm hoặc chọn loại giao dịch khác.'
                      : 'Hệ thống chưa ghi nhận biến động số dư nào.'}
                  </div>
                </td>
              </tr>
            ) : (
              transactions.map((tx) => {
                const isPositive = tx.amount > 0;
                const isNegative = tx.amount < 0;
                const formattedAmount = `${isPositive ? '+' : ''}${tx.amount.toLocaleString('vi-VN')} Xu`;
                const formattedBalance = `${(tx.balanceAfter ?? 0).toLocaleString('vi-VN')} Xu`;
                const refCode = tx.referenceId || tx.id;
                const isCopied = copiedId === (tx.id || refCode);
                const ssoUidText = tx.ssoUid || tx.userId || 'N/A';

                return (
                  <tr key={tx.id || tx._id} className="hover:bg-indigo-50/30 transition-colors">
                    {/* 1. Thời gian */}
                    <td className="py-3.5 px-4 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                      {tx.createdAt ? new Date(tx.createdAt).toLocaleString('vi-VN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }) : '—'}
                    </td>

                    {/* 2. Người dùng (Avatar, Tên, Email, ssoUid copy) */}
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3 max-w-[260px]">
                        {/* Avatar bên trái */}
                        <div className="relative shrink-0">
                          {tx.userAvatar ? (
                            <img
                              src={tx.userAvatar}
                              alt={tx.userName || tx.userEmail}
                              className="w-9 h-9 rounded-full object-cover border border-slate-200 shadow-2xs"
                              onError={(e) => {
                                // Fallback nếu link ảnh lỗi
                                (e.target as HTMLElement).style.display = 'none';
                                const fallback = (e.target as HTMLElement).nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold text-xs flex items-center justify-center border border-slate-200 shadow-2xs ${
                              tx.userAvatar ? 'hidden' : 'flex'
                            }`}
                          >
                            {(tx.userName || tx.userEmail || 'U').charAt(0).toUpperCase()}
                          </div>
                        </div>

                        {/* Thông tin Text bên phải */}
                        <div className="min-w-0 flex-1 space-y-0.5">
                          {/* Dòng 1: Tên hiển thị in đậm, màu chữ chính */}
                          <div className="font-extrabold text-slate-900 text-xs truncate leading-tight" title={tx.userName || 'Thành viên'}>
                            {tx.userName || 'Thành viên'}
                          </div>

                          {/* Dòng 2: Email màu xám nhạt kèm nút copy ssoUid gọn gàng */}
                          <div className="flex items-center space-x-1.5 text-[11px] text-gray-500 leading-tight">
                            <span className="truncate max-w-[145px]" title={tx.userEmail}>
                              {tx.userEmail}
                            </span>
                            
                            {/* Nút copy ssoUid tinh tế */}
                            <button
                              type="button"
                              onClick={() => handleCopy(ssoUidText, `uid-${tx.id}`)}
                              className="group relative p-0.5 text-gray-400 hover:text-indigo-600 hover:bg-slate-100 rounded transition-all shrink-0 cursor-pointer"
                              title={`Sao chép ssoUid: ${ssoUidText}`}
                            >
                              {copiedId === `uid-${tx.id}` ? (
                                <span className="flex items-center space-x-0.5 text-[10px] text-emerald-600 font-semibold">
                                  <Check className="w-3 h-3" />
                                </span>
                              ) : (
                                <Copy className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* 3. Loại Giao Dịch */}
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      {renderTypeBadge(tx.type)}
                    </td>

                    {/* 4. Tham chiếu (Ref) */}
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="inline-flex items-center space-x-1.5 bg-slate-50 hover:bg-indigo-50/50 border border-slate-200/80 px-2 py-1 rounded-lg transition-colors">
                        <span className="font-mono font-bold text-[11px] text-slate-700">
                          {refCode}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopy(refCode, tx.id || refCode)}
                          className="p-1 text-slate-400 hover:text-indigo-600 rounded transition-colors cursor-pointer"
                          title="Sao chép mã tham chiếu"
                        >
                          {isCopied ? (
                            <Check className="w-3.5 h-3.5 text-emerald-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </td>

                    {/* 5. Biến động */}
                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      <span className={`inline-block font-extrabold text-xs px-2.5 py-1 rounded-lg border font-mono ${
                        isPositive
                          ? 'text-emerald-700 bg-emerald-50/90 border-emerald-200'
                          : isNegative
                          ? 'text-rose-700 bg-rose-50/90 border-rose-200'
                          : 'text-slate-600 bg-slate-100 border-slate-200'
                      }`}>
                        {formattedAmount}
                      </span>
                    </td>

                    {/* 6. Số dư cuối */}
                    <td className="py-3.5 px-4 text-right whitespace-nowrap font-mono font-bold text-slate-900 text-xs">
                      {formattedBalance}
                    </td>

                    {/* 7. Chi tiết */}
                    <td className="py-3.5 px-4 text-slate-600 font-medium leading-relaxed">
                      <div className="line-clamp-2" title={tx.description}>
                        {tx.description || 'Không có mô tả'}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 3. Server Pagination */}
      <ServerPagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        itemLabel="giao dịch"
        themeColor="purple"
      />
    </div>
  );
};

export default LedgerTable;
